import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, poundsFromPence, formatErr } from "../lib/api";
import { PublicShell } from "./PublicLandingPage";
import { mergeBranding } from "../lib/templates";
import { CheckCircle2, Mail, Phone, Send, Share2, Check } from "lucide-react";

function fmtVisitDate(ds) {
  if (!ds) return "";
  const [y, m, d] = ds.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

export default function ConfirmationPage() {
  const { slug, orderId } = useParams();
  const [data, setData] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams(window.location.search);
    const cameFromStripe = params.has("payment_intent") || params.get("redirect_status");
    const loadOrder = async () => {
      const r = await api.get(`/orders/${orderId}/public`);
      if (cancelled) return;
      setData(r.data);
    };
    const run = async () => {
      if (cameFromStripe) {
        setProcessing(true);
        for (let i = 0; i < 15 && !cancelled; i++) {
          try {
            const s = await api.get(`/orders/${orderId}/status`);
            const st = s.data.status;
            if (st === "confirmed") { await loadOrder(); if (!cancelled) setProcessing(false); return; }
            if (st === "failed") { if (!cancelled) { setFailed(true); setProcessing(false); } return; }
          } catch { /* keep polling */ }
          await new Promise((res) => setTimeout(res, 1200));
        }
        try { await loadOrder(); } catch { /* noop */ }
        if (!cancelled) setProcessing(false);
      } else { await loadOrder(); }
    };
    run();
    return () => { cancelled = true; };
  }, [orderId]);

  if (failed) {
    return (
      <PublicShell>
        <div className="text-center py-32" data-testid="payment-failed">
          <h1 className="text-3xl font-bold text-[#fca5a5]">Payment failed</h1>
          <p className="text-[#bbb] mt-3">Your payment could not be completed and you have not been charged.</p>
          <Link to={`/${slug}`} className="st-link inline-block mt-6">Back to booking</Link>
        </div>
      </PublicShell>
    );
  }
  if (processing && !data) {
    return (
      <PublicShell>
        <div className="text-center py-32 text-[#8a8a8a]" data-testid="payment-processing">
          <div className="inline-block w-8 h-8 border-2 border-[#dc2626] border-t-transparent rounded-full animate-spin mb-4" />
          <div>Confirming your payment…</div>
        </div>
      </PublicShell>
    );
  }
  if (!data) return <PublicShell><div className="text-center py-32 text-[#8a8a8a]">Loading…</div></PublicShell>;

  const { order, attraction, attendees, event_date, time_slot } = data;
  const b = mergeBranding(attraction?.branding);
  const heading = b.confirmation_heading || "Booking Confirmed!";
  const subheading = b.confirmation_subheading || `Your tickets are on their way to ${order.customer_email}.`;
  const orderHeading = b.confirmation_order_heading || "Order Summary";
  const ticketHeading = b.confirmation_ticket_heading || "Your Tickets";
  const timeText = time_slot ? `${time_slot.start_time}${time_slot.end_time ? "–" + time_slot.end_time : ""}` : null;

  return (
    <PublicShell attraction={attraction}>
      <div className="max-w-2xl mx-auto px-6 py-16 st-fade-up public-themed">
        <div className="text-center">
          <div className="inline-flex w-20 h-20 rounded-full bg-[var(--accent-color,#dc2626)] items-center justify-center st-pulse">
            <CheckCircle2 size={40} />
          </div>
          <h1 className="confirmation-heading mt-6" data-testid="confirmation-heading">{heading}</h1>
          <p className="confirmation-subheading mt-2 text-sm" data-testid="confirmation-subheading">{subheading}</p>
        </div>

        {/* Order summary box */}
        <div className="confirmation-order-box mt-8" data-testid="confirmation-order-box">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm uppercase tracking-[0.18em] opacity-70">{orderHeading}</h3>
            <span className="st-mono text-sm" style={{ color: "var(--accent-color,#dc2626)" }}>#{order.order_number}</span>
          </div>
          <div className="text-sm space-y-1.5">
            {event_date?.date && <Row label="Visit date" value={fmtVisitDate(event_date.date)} />}
            {timeText && <Row label="Time slot" value={timeText} />}
            {(order.line_items || []).map((li, i) => (
              <Row key={i} label={`${li.quantity}× ${li.name}`} value={poundsFromPence((li.unit_price_pence || 0) * li.quantity)} />
            ))}
            {(order.addon_items || []).map((ad, i) => (
              <Row key={`a${i}`} label={`${ad.quantity}× ${ad.name}`} value={poundsFromPence((ad.unit_price_pence || 0) * ad.quantity)} />
            ))}
          </div>
          <div className="h-px bg-white/10 my-3" />
          <div className="flex justify-between font-bold"><span>Total paid</span><span>{poundsFromPence(order.gross_amount)}</span></div>
        </div>

        {/* Ticket list box */}
        <div className="confirmation-ticket-box mt-5" data-testid="confirmation-attendees">
          <h3 className="text-sm uppercase tracking-[0.18em] opacity-70 mb-4">{ticketHeading} ({attendees.length})</h3>
          <div className="space-y-2">
            {attendees.map((a) => (
              <Link key={a.id} to={`/ticket/${a.id}`} target="_blank"
                    className="flex items-center justify-between bg-black/20 border border-white/10 hover:border-[var(--accent-color,#dc2626)] rounded-lg p-4 transition-colors">
                <div>
                  <div className="font-semibold">{a.name}</div>
                  <div className="text-xs opacity-60">{a.ticket_type_name}</div>
                </div>
                <span className="st-link text-sm">Open ticket →</span>
              </Link>
            ))}
          </div>
        </div>

        <SendTicketsSection order={order} attraction={attraction} attendees={attendees} eventDate={event_date} timeText={timeText} />

        <div className="text-center mt-6">
          <Link to={`/${slug}`} className="st-link">Back to {attraction?.name}</Link>
        </div>
      </div>
    </PublicShell>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between gap-3"><span className="opacity-70">{label}</span><span className="text-right">{value}</span></div>;
}

function SendTicketsSection({ order, attraction, attendees, eventDate, timeText }) {
  const [skipped, setSkipped] = useState(false);
  const [rows, setRows] = useState(() => attendees.reduce((acc, a, i) => {
    acc[a.id] = { email: i === 0 ? order.customer_email : "", phone: i === 0 ? (order.customer_phone || "") : "", showPhone: false, sent: false, busy: false };
    return acc;
  }, {}));

  if (skipped) return null;

  const setRow = (id, patch) => setRows((p) => ({ ...p, [id]: { ...p[id], ...patch } }));

  const send = async (att) => {
    const row = rows[att.id];
    if (!row.email && !row.phone) return;
    setRow(att.id, { busy: true });
    try {
      await api.post(`/orders/${order.id}/send-ticket`, { attendee_id: att.id, email: row.email || null, phone: row.phone || null });
      setRow(att.id, { sent: true, busy: false });
      setTimeout(() => setRow(att.id, { sent: false }), 3000);
    } catch (e) { setRow(att.id, { busy: false }); }
  };

  const sendAll = async () => {
    for (const att of attendees) {
      const row = rows[att.id];
      if (row.email || row.phone) await send(att);
    }
  };

  const shareWhatsApp = (att) => {
    const url = `${window.location.origin}/ticket/${att.id}`;
    const dateStr = fmtVisitDate(eventDate?.date);
    const message = `Here's your ticket for ${attraction.name}${dateStr ? ` on ${dateStr}` : ""}${timeText ? ` at ${timeText}` : ""}: ${url}`;
    if (navigator.share) {
      navigator.share({ title: `Your ticket for ${attraction.name}`, text: message, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank");
    }
  };

  const anyContact = attendees.some((a) => rows[a.id]?.email || rows[a.id]?.phone);

  return (
    <div className="confirmation-ticket-box mt-5" data-testid="send-tickets-section">
      <h3 className="text-base font-semibold">Send tickets to your group</h3>
      <p className="text-sm opacity-60 mb-4">Share an individual ticket with each person.</p>
      <div className="space-y-4">
        {attendees.map((a, i) => {
          const row = rows[a.id];
          return (
            <div key={a.id} className="border-b border-white/10 pb-4 last:border-0 last:pb-0" data-testid={`send-row-${a.id}`}>
              <div className="flex items-center gap-2 text-sm font-medium">
                <span className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-xs">{i + 1}</span>
                {a.name} <span className="opacity-50 font-normal">· {a.ticket_type_name}</span>
                {i === 0 && <span className="text-[10px] uppercase tracking-wider opacity-50">(you)</span>}
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <div className="relative flex-1 min-w-[180px]">
                  <Mail size={13} className="absolute left-2.5 top-2.5 opacity-50" />
                  <input className="st-input pl-8 text-sm w-full" placeholder="Email address" value={row.email}
                         onChange={(e) => setRow(a.id, { email: e.target.value })} data-testid={`send-email-${a.id}`} />
                </div>
                {row.showPhone && (
                  <div className="relative flex-1 min-w-[160px]">
                    <Phone size={13} className="absolute left-2.5 top-2.5 opacity-50" />
                    <input className="st-input pl-8 text-sm w-full" placeholder="Phone (optional)" value={row.phone}
                           onChange={(e) => setRow(a.id, { phone: e.target.value })} data-testid={`send-phone-${a.id}`} />
                  </div>
                )}
                {!row.showPhone && (
                  <button className="st-btn st-btn-ghost text-xs" onClick={() => setRow(a.id, { showPhone: true })} data-testid={`send-phone-toggle-${a.id}`} title="Add phone">
                    <Phone size={13} />
                  </button>
                )}
                <button className="st-btn text-xs" disabled={row.busy || (!row.email && !row.phone)} onClick={() => send(a)} data-testid={`send-btn-${a.id}`}>
                  {row.sent ? <><Check size={13} /> Sent</> : <><Send size={13} /> Send</>}
                </button>
                <button className="st-btn st-btn-ghost text-xs" onClick={() => shareWhatsApp(a)} data-testid={`whatsapp-btn-${a.id}`} title="Share via WhatsApp / share sheet">
                  <Share2 size={13} /> WhatsApp
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between mt-4">
        <button className="text-xs opacity-60 hover:opacity-100 underline" onClick={() => setSkipped(true)} data-testid="send-skip">Skip — I'll share tickets later</button>
        <button className="st-btn text-sm" disabled={!anyContact} onClick={sendAll} data-testid="send-all-btn"><Send size={14} /> Send all tickets</button>
      </div>
    </div>
  );
}
